import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, GitPullRequestArrow, Plus, RotateCcw, Building2, User, Clock, Sparkles, Gauge, Check } from "lucide-react";
import { REF_STATUS_HEX, PRIORITY_HEX, pretty, hexA, fmtDate } from "./external/referralConstants";
import DigestCard from "@/components/external/DigestCard";

const StatusChip = ({ status, testid }) => {
  const hex = REF_STATUS_HEX[status] || REF_STATUS_HEX.draft;
  return <span data-testid={testid} className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
    style={{ backgroundColor: hexA(hex, 0.14), color: hex }}>{pretty(status)}</span>;
};

const EMPTY = {
  external_account_id: "", referral_type: "general", priority: "normal", summary: "",
  client_name: "", client_phone: "", client_email: "", client_address: "",
  external_note: "", internal_note: "", send_now: true,
};

const BUCKET_LABELS = {
  waiting: "Waiting on partner", accepted_today: "Accepted today", declined_today: "Declined today",
  follow_up: "Follow-up overdue", quote_pending: "Quote / estimate pending",
  quote_uploaded: "Quote / estimate uploaded",
  tour_pending: "Tour / appointment pending", outcome_pending: "Outcome confirmation pending",
  stalled: "Stalled (7+ days)", new_messages: "New partner messages",
  sla_nudge: "Awaiting partner response",
};

export default function StaffExternalReferrals() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const bucket = sp.get("bucket") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ status: sp.get("status") || "all", account_type: sp.get("account_type") || "all" });
  const [meta, setMeta] = useState({ statuses: [], priorities: [], referral_types: [] });
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [nu, setNu] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [suggType, setSuggType] = useState("vendor");
  const [suggLane, setSuggLane] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  const [suggLoading, setSuggLoading] = useState(false);
  const [showSugg, setShowSugg] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        status: f.status === "all" ? undefined : f.status,
        account_type: f.account_type === "all" ? undefined : f.account_type,
        bucket: bucket || undefined,
      };
      const { data } = await api.get("/ext-referrals", { params });
      setData(data);
    } catch { toast.error("Could not load external referrals."); }
    finally { setLoading(false); }
  }, [f, bucket]);
  useEffect(() => { load(); }, [load]);

  const clearBucket = () => { const n = new URLSearchParams(sp); n.delete("bucket"); setSp(n); };

  useEffect(() => {
    api.get("/ext-referrals/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/external-accounts").then(({ data }) => setAccounts((data.items || []).filter((a) => a.status !== "revoked"))).catch(() => {});
  }, []);

  const loadSuggestions = useCallback(async () => {
    setSuggLoading(true);
    try {
      const params = { account_type: suggType, service_lane: suggLane || undefined };
      const { data } = await api.get("/ext-referral-suggestions", { params });
      setSuggestions(data.suggestions || []);
    } catch { setSuggestions([]); } finally { setSuggLoading(false); }
  }, [suggType, suggLane]);
  useEffect(() => { if (open && showSugg) loadSuggestions(); }, [open, showSugg, loadSuggestions]);

  const pickSuggestion = (s) => {
    setNu((p) => ({ ...p, external_account_id: s.account_id, from_recommendation: true, recommendation_rank: s.rank }));
    toast.success(`Selected ${s.organization}.`);
  };

  const create = async () => {
    if (!nu.external_account_id) { toast.error("Choose an external account."); return; }
    setSaving(true);
    try {
      const client = (nu.client_name || nu.client_phone || nu.client_email || nu.client_address)
        ? { name: nu.client_name, phone: nu.client_phone, email: nu.client_email, address: nu.client_address } : null;
      const payload = {
        external_account_id: nu.external_account_id, referral_type: nu.referral_type,
        priority: nu.priority, summary: nu.summary || undefined, shared_client: client,
        external_note: nu.external_note || undefined, internal_note: nu.internal_note || undefined,
        send_now: nu.send_now,
        from_recommendation: !!nu.from_recommendation,
        recommendation_rank: nu.from_recommendation ? nu.recommendation_rank : undefined,
      };
      const { data } = await api.post("/ext-referrals", payload);
      toast.success(nu.send_now ? "Referral created and sent." : "Draft referral created.");
      setOpen(false); setNu(EMPTY);
      nav(`/external-referrals/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const items = data?.items || [];
  const counts = data?.counts || {};
  const accountType = (a) => (a.account_type_label || pretty(a.account_type || "partner"));
  const accLabel = (a) => `${a.organization || a.full_name} (${accountType(a)})`;

  return (
    <div className="pb-12" data-testid="staff-external-referrals">
      <DigestCard />
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <GitPullRequestArrow size={22} className="text-[#1C4358]" />
          <h1 className="text-2xl font-display font-bold text-[#1C4358]">External Referrals</h1>
          {data && <span className="text-[13px] text-[#4B5563]">{items.length} shown</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="border-[#D6E0E7] text-[#1C4358] gap-1"><RotateCcw size={14} /> Refresh</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="ext-ref-new-btn" className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1"><Plus size={15} /> New Referral</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="ext-ref-create-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-[#1C4358]">New External Referral</DialogTitle>
                <DialogDescription className="text-[13px]">Share a referral with a partner, vendor, facility or professional. Internal notes stay staff-only.</DialogDescription>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 max-h-[62vh] overflow-y-auto pr-1">
                {/* PROMPT 12E — smart routing nudge (staff decision-support only) */}
                <div className="sm:col-span-2 rounded-xl border border-[#1C4358]/20 bg-[#1C4358]/4 p-3" data-testid="smart-routing-panel">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-[12px] font-bold text-[#1C4358] flex items-center gap-1.5"><Sparkles size={14} /> Recommended partners</div>
                    <button type="button" onClick={() => setShowSugg((v) => !v)} className="text-[11px] text-[#9C1E22] font-medium" data-testid="smart-routing-toggle">{showSugg ? "Skip suggestions" : "Show suggestions"}</button>
                  </div>
                  {showSugg && (
                    <>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <Select value={suggType} onValueChange={setSuggType}>
                          <SelectTrigger className="h-8 w-[150px] text-[12px]" data-testid="smart-routing-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="partner">Partner</SelectItem>
                            <SelectItem value="vendor">Vendor</SelectItem>
                            <SelectItem value="facility">Facility</SelectItem>
                            <SelectItem value="professional">Professional</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input value={suggLane} onChange={(e) => setSuggLane(e.target.value)} placeholder="Service lane / category (optional)" className="h-8 text-[12px] flex-1 min-w-[140px]" data-testid="smart-routing-lane" />
                        <Button type="button" size="sm" variant="outline" onClick={loadSuggestions} disabled={suggLoading} className="h-8 text-[12px] border-[#D6E0E7] text-[#1C4358]">{suggLoading ? <Loader2 size={13} className="animate-spin" /> : "Find"}</Button>
                      </div>
                      {suggLoading ? <div className="text-[12px] text-muted-foreground py-2">Finding best-fit partners…</div> :
                        (suggestions || []).length === 0 ? <div className="text-[12px] text-muted-foreground py-2" data-testid="smart-routing-empty">No matching partners found. Choose an account below.</div> : (
                          <div className="space-y-2">
                            {suggestions.map((s) => {
                              const selected = nu.external_account_id === s.account_id;
                              return (
                                <div key={s.account_id} data-testid={`smart-routing-card-${s.account_id}`}
                                  className={`rounded-lg border p-2.5 bg-white ${selected ? "border-[#2F7D5A] ring-1 ring-[#2F7D5A]/30" : "border-[#E6EDF2]"}`}>
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[10px] font-bold text-[#9AA5B1]">#{s.rank}</span>
                                        <span className="text-[13px] font-semibold text-[#1C4358]">{s.organization}</span>
                                        <span className="text-[10px] text-[#9AA5B1]">{s.account_type_label}</span>
                                      </div>
                                      <div className="text-[11px] text-[#4B5563] mt-0.5">{s.why}</div>
                                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-[#9AA5B1] mt-1">
                                        <span>Accept {Math.round((s.acceptance_rate || 0) * 100)}%</span>
                                        {s.avg_time_to_accept && <span>~{s.avg_time_to_accept} to accept</span>}
                                        {s.avg_time_to_quote && <span>~{s.avg_time_to_quote} to quote</span>}
                                        <span>Open {s.open}</span>
                                        {s.overdue > 0 && <span className="text-[#9C1E22]">Overdue {s.overdue}</span>}
                                        <span>Agreement: {pretty(s.agreement_status)}</span>
                                      </div>
                                    </div>
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                                      style={{ backgroundColor: s.responsiveness === "excellent" ? "#2F7D5A22" : s.responsiveness === "slow" ? "#9C1E2222" : "#2C648022",
                                               color: s.responsiveness === "excellent" ? "#2F7D5A" : s.responsiveness === "slow" ? "#9C1E22" : "#2C6480" }}>{s.responsiveness_label}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-2">
                                    <Button type="button" size="sm" onClick={() => pickSuggestion(s)}
                                      className={`h-7 text-[11px] gap-1 ${selected ? "bg-[#2F7D5A] hover:bg-[#276849]" : "bg-[#1C4358] hover:bg-[#153341]"} text-white`} data-testid={`smart-routing-select-${s.account_id}`}>
                                      {selected ? <><Check size={12} /> Selected</> : "Select this partner"}
                                    </Button>
                                    {s.relationship_id && <button type="button" onClick={() => nav(`/crm/relationships/${s.relationship_id}`)} className="text-[11px] text-[#1C4358] hover:underline">View relationship</button>}
                                    <button type="button" onClick={() => nav("/reports/external-referrals")} className="text-[11px] text-[#1C4358] hover:underline flex items-center gap-0.5"><Gauge size={11} /> Scorecard</button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[11px] text-[#4B5563]">External account *</label>
                  <Select value={nu.external_account_id} onValueChange={(v) => setNu({ ...nu, external_account_id: v })}>
                    <SelectTrigger data-testid="ext-ref-new-account" className="h-9 text-[13px]"><SelectValue placeholder="Choose partner / vendor / facility / professional" /></SelectTrigger>
                    <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{accLabel(a)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] text-[#4B5563]">Type</label>
                  <Select value={nu.referral_type} onValueChange={(v) => setNu({ ...nu, referral_type: v })}>
                    <SelectTrigger data-testid="ext-ref-new-type" className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta.referral_types || []).map((t) => <SelectItem key={t} value={t}>{pretty(t)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] text-[#4B5563]">Priority</label>
                  <Select value={nu.priority} onValueChange={(v) => setNu({ ...nu, priority: v })}>
                    <SelectTrigger data-testid="ext-ref-new-priority" className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta.priorities || []).map((p) => <SelectItem key={p} value={p}>{pretty(p)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><label className="text-[11px] text-[#4B5563]">Summary</label><Textarea data-testid="ext-ref-new-summary" value={nu.summary} onChange={(e) => setNu({ ...nu, summary: e.target.value })} rows={2} className="text-[14px]" placeholder="What is this referral about?" /></div>
                <div className="sm:col-span-2 text-[11px] font-semibold text-[#1C4358] uppercase tracking-wide pt-1">Client details shared with this account</div>
                <div><label className="text-[11px] text-[#4B5563]">Client name</label><Input value={nu.client_name} onChange={(e) => setNu({ ...nu, client_name: e.target.value })} className="h-9 text-[14px]" /></div>
                <div><label className="text-[11px] text-[#4B5563]">Client phone</label><Input value={nu.client_phone} onChange={(e) => setNu({ ...nu, client_phone: e.target.value })} className="h-9 text-[14px]" /></div>
                <div><label className="text-[11px] text-[#4B5563]">Client email</label><Input value={nu.client_email} onChange={(e) => setNu({ ...nu, client_email: e.target.value })} className="h-9 text-[14px]" /></div>
                <div><label className="text-[11px] text-[#4B5563]">Client address</label><Input value={nu.client_address} onChange={(e) => setNu({ ...nu, client_address: e.target.value })} className="h-9 text-[14px]" /></div>
                <div className="sm:col-span-2"><label className="text-[11px] text-[#4B5563]">Message to partner (visible to them)</label><Textarea data-testid="ext-ref-new-ext-note" value={nu.external_note} onChange={(e) => setNu({ ...nu, external_note: e.target.value })} rows={2} className="text-[14px]" /></div>
                <div className="sm:col-span-2"><label className="text-[11px] text-[#9C1E22]">Internal note (staff only — never shown to partner)</label><Textarea data-testid="ext-ref-new-int-note" value={nu.internal_note} onChange={(e) => setNu({ ...nu, internal_note: e.target.value })} rows={2} className="text-[14px]" /></div>
                <label className="sm:col-span-2 flex items-center gap-2 text-[13px] text-[#252221]">
                  <input type="checkbox" checked={nu.send_now} onChange={(e) => setNu({ ...nu, send_now: e.target.checked })} data-testid="ext-ref-new-sendnow" /> Send to the account immediately (otherwise save as draft)
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="border-[#D6E0E7] text-[#1C4358]">Cancel</Button>
                <Button data-testid="ext-ref-create-submit" onClick={create} disabled={saving} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1">{saving && <Loader2 size={14} className="animate-spin" />} Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Active bucket banner (from Command Center click-through) */}
      {bucket && (
        <div className="flex items-center gap-2 mb-3 rounded-lg bg-[#1C4358]/8 border border-[#1C4358]/20 px-3 py-2" data-testid="ext-ref-bucket-banner">
          <span className="text-[13px] text-[#1C4358] font-semibold">Showing: {BUCKET_LABELS[bucket] || pretty(bucket)}</span>
          <button onClick={clearBucket} className="text-[12px] text-[#9C1E22] font-medium hover:underline ml-1" data-testid="ext-ref-bucket-clear">Clear</button>
        </div>
      )}

      {/* Status counts */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {Object.entries(counts).map(([s, n]) => {
          const hex = REF_STATUS_HEX[s] || "#6B7280";
          return (
            <button key={s} data-testid={`ext-ref-count-${s}`} onClick={() => setF((p) => ({ ...p, status: p.status === s ? "all" : s }))}
              className="text-[12px] font-semibold px-2.5 py-1 rounded-full border transition-colors"
              style={{ borderColor: hexA(hex, 0.3), color: hex, backgroundColor: f.status === s ? hexA(hex, 0.12) : "white" }}>
              {pretty(s)} <span className="tabular-nums">{n}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={f.account_type} onValueChange={(v) => setF((p) => ({ ...p, account_type: v }))}>
          <SelectTrigger data-testid="ext-ref-filter-type" className="w-[190px] h-9 text-[13px]"><SelectValue placeholder="All account types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All account types</SelectItem>
            <SelectItem value="partner">Partner</SelectItem>
            <SelectItem value="vendor">Vendor</SelectItem>
            <SelectItem value="facility">Facility</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
          </SelectContent>
        </Select>
        <Select value={f.status} onValueChange={(v) => setF((p) => ({ ...p, status: v }))}>
          <SelectTrigger data-testid="ext-ref-filter-status" className="w-[190px] h-9 text-[13px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(meta.statuses || []).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && !data ? <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div> :
        items.length === 0 ? <div className="text-center py-16 text-[#4B5563]"><GitPullRequestArrow className="mx-auto mb-2 text-[#9AA5B1]" /> No external referrals yet. Create one to share with a partner, vendor, facility or professional.</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((r) => (
              <div key={r.id} data-testid={`ext-ref-card-${r.id}`} onClick={() => nav(`/external-referrals/${r.id}`)}
                className="rounded-xl border border-[#E6EDF2] bg-white p-4 hover:border-[#1C4358]/40 transition-colors cursor-pointer">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-[#1C4358] text-[14px] leading-tight flex items-center gap-1"><Building2 size={13} /> {r.external_org || "—"}</span>
                  <div className="flex items-center gap-1">
                    {r.is_overdue && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#9C1E22]/12 text-[#9C1E22]" data-testid={`ext-ref-overdue-${r.id}`}>Overdue</span>}
                    {r.sla_overdue && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#B7791F]/14 text-[#B7791F]" data-testid={`ext-ref-sla-${r.id}`}>Awaiting</span>}
                    <StatusChip status={r.status} testid={`ext-ref-status-${r.id}`} />
                  </div>
                </div>
                <div className="text-[12px] text-[#4B5563] mt-1">{r.account_type_label} · {pretty(r.referral_type)}
                  {r.priority && r.priority !== "normal" && <span className="ml-1 font-bold" style={{ color: PRIORITY_HEX[r.priority] }}>· {pretty(r.priority)}</span>}
                </div>
                {r.summary && <p className="text-[13px] text-[#252221] mt-2 line-clamp-2">{r.summary}</p>}
                {r.shared_client?.name && <div className="text-[12px] text-[#4B5563] mt-1.5 flex items-center gap-1"><User size={11} /> {r.shared_client.name}</div>}
                <div className="text-[11px] text-[#9AA5B1] mt-2 flex items-center gap-1"><Clock size={11} /> {fmtDate(r.updated_at)}</div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
